import React from 'react';
import {
  PageDefaultIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchDefaultIcon,
  SearchableList,
  SearchableListBox,
  SearchableListInput,
  SelectTrigger,
  View,
  OptionType,
} from '@packages/systems/spring';
import {PageListNodeType} from '@packages/systems/core/records/PageRecord';
import type {SearchableListPage} from '../RedirectSection';
import PageSelectOption from './PageSelectOption';

const PAGE_SELECT_POPOVER_WIDTH = '220px';
const PAGE_SELECT_POPOVER_MAX_HEIGHT = '300px';

type RedirectPageSelectProps = {
  selectedPage?: {
    page?: PageListNodeType;
    url?: string;
  };
  onPageSelect: (url: string) => void;
  staticPagesOptions: SearchableListPage[];
};

const RedirectPageSelect = ({
  selectedPage,
  onPageSelect,
  staticPagesOptions,
}: RedirectPageSelectProps) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [buttonText, setButtonText] = React.useState<string>('');

  React.useEffect(() => {
    setButtonText(selectedPage?.page?.name || 'Select page');
  }, [selectedPage]);

  const updateSelection = React.useCallback(
    (selectedPageId: string) => {
      onPageSelect(selectedPageId);
      setIsOpen(false);
    },
    [onPageSelect, setIsOpen]
  );

  const handleRenderOption = React.useCallback((pageOption: OptionType) => {
    return <PageSelectOption option={pageOption} />;
  }, []);

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      minHeight="fit-content"
      width={PAGE_SELECT_POPOVER_WIDTH}
    >
      <PopoverTrigger asChild onClick={() => setIsOpen(!isOpen)}>
        <SelectTrigger
          data-automation-id="redirect-page-select-trigger"
          prefix={<PageDefaultIcon />}
        >
          {buttonText}
        </SelectTrigger>
      </PopoverTrigger>
      <PopoverContent
        isSeamless
        colorVariant="tertiary"
        data-automation-id="redirect-page-popover"
      >
        <SearchableList
          options={staticPagesOptions}
          selectionMode="single"
          onChange={updateSelection}
          renderOption={handleRenderOption}
        >
          <View padding="$2">
            <SearchableListInput
              placeholder="Search pages"
              prefix={SearchDefaultIcon}
            />
          </View>
          <SearchableListBox
            isVirtualized
            maxHeight={PAGE_SELECT_POPOVER_MAX_HEIGHT}
          />
        </SearchableList>
      </PopoverContent>
    </Popover>
  );
};

export default RedirectPageSelect;
